"use client";

/**
 * IntercomFacade - Lightweight chat button placeholder
 *
 * Shows a CSS-only chat button before Intercom loads.
 * When clicked, triggers Intercom to load and show.
 * Automatically hides once real Intercom launcher appears.
 *
 * CRITICAL: Dimensions and POSITIONING must EXACTLY match Intercom's launcher to prevent layout shift
 * Intercom launcher: 48x48px visible, positioned bottom:20px right:20px (desktop), bottom:70px right:20px (mobile)
 * See globals.css for mobile positioning override on real Intercom launcher
 */
import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { useIntercom } from "./IntercomProvider";
interface IntercomFacadeProps {
  className?: string;
}

// Helper to check for Intercom launcher elements
function checkForIntercomLauncher(): boolean {
  return !!(document.querySelector(".intercom-lightweight-app-launcher") || document.querySelector(".intercom-launcher-frame") || document.querySelector(".intercom-launcher") || document.querySelector('iframe[name="intercom-launcher-frame"]'));
}
export function IntercomFacade(t0) {
  const $ = _c(19);
  const {
    className
  } = t0;
  const {
    show,
    isLoaded,
    isLoading
  } = useIntercom();
  const [isHidden, setIsHidden] = useState(false);
  let t1;
  let t2;
  if ($[0] !== isHidden || $[1] !== isLoading) {
    t1 = () => {
      if (!isLoading || isHidden) {
        return;
      }
      if (checkForIntercomLauncher()) {
        setIsHidden(true);
        return;
      }
      const interval = setInterval(() => {
        if (checkForIntercomLauncher()) {
          setIsHidden(true);
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    };
    t2 = [isLoading, isHidden];
    $[0] = isHidden;
    $[1] = isLoading;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== isHidden || $[5] !== isLoaded) {
    t3 = () => {
      if (!isLoaded || isHidden) {
        return;
      }
      if (checkForIntercomLauncher()) {
        setIsHidden(true);
        return;
      }
      const interval_0 = setInterval(() => {
        if (checkForIntercomLauncher()) {
          setIsHidden(true);
          clearInterval(interval_0);
        }
      }, 100);
      const timeout = setTimeout(() => {
        clearInterval(interval_0);
        setIsHidden(true);
      }, 2000);
      return () => {
        clearInterval(interval_0);
        clearTimeout(timeout);
      };
    };
    t4 = [isLoaded, isHidden];
    $[4] = isHidden;
    $[5] = isLoaded;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== show) {
    t5 = () => {
      show();
    };
    $[8] = show;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleClick = t5;
  if (isHidden) {
    return null;
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <style>{"\n                @media (max-width: 640px) {\n                    html[data-demo] #intercom-facade-btn {\n                        bottom: 70px !important;\n                    }\n                }\n                @keyframes intercom-facade-spin { to { transform: rotate(360deg); } }\n            "}</style>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      position: "fixed",
      bottom: "20px",
      right: "20px",
      zIndex: 2147483000,
      width: "48px",
      height: "48px",
      padding: 0,
      margin: 0,
      borderRadius: "50%",
      backgroundColor: "#141414",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "rgba(0, 0, 0, 0.06) 0px 1px 6px 0px, rgba(0, 0, 0, 0.16) 0px 2px 32px 0px",
      transition: "transform 0.15s ease, box-shadow 0.15s ease"
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isLoading) {
    t8 = isLoading ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{
      animation: "intercom-facade-spin 1s linear infinite"
    }}><circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" fill="none" /><path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" fill="none" /></svg> : <svg width="24" height="24" viewBox="0 0 28 32" fill="white"><path d="M28,32 C28,32 23.2863266,30.1450667 19.4727818,28.6592 L3.43749107,28.6592 C1.53921989,28.6592 0,27.0272 0,25.0144 L0,3.6448 C0,1.632 1.53921989,0 3.43749107,0 L24.5615088,0 C26.45978,0 27.9989999,1.632 27.9989999,3.6448 L27.9989999,22.0490667 L28,22.0490667 L28,32 Z M23.8614088,20.0181333 C23.5309223,19.6105242 22.9540812,19.5633836 22.5692242,19.9125333 C22.5392199,19.9392 19.5537934,22.5941333 13.9989999,22.5941333 C8.51321617,22.5941333 5.48178311,19.9584 5.4277754,19.9104 C5.04295119,19.5629428 4.46760991,19.6105095 4.13759108,20.0170667 C3.97913051,20.2124916 3.9004494,20.4673395 3.91904357,20.7249415 C3.93763774,20.9825435 4.05196575,21.2215447 4.23660523,21.3888 C4.37862552,21.5168 7.77411059,24.5386667 13.9989999,24.5386667 C20.2248893,24.5386667 23.6203743,21.5168 23.7623946,21.3888 C23.9467342,21.2215726 24.0608642,20.9827905 24.0794539,20.7254507 C24.0980436,20.4681109 24.0195551,20.2135019 23.8614088,20.0181333 Z" /></svg>;
    $[12] = isLoading;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== className || $[15] !== handleClick || $[16] !== isLoading || $[17] !== t8) {
    t9 = <>{t6}<button id="intercom-facade-btn" onClick={handleClick} disabled={isLoading} aria-label="Open chat support" className={className} style={t7} onMouseEnter={_temp} onMouseLeave={_temp2}>{t8}</button></>;
    $[14] = className;
    $[15] = handleClick;
    $[16] = isLoading;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}
function _temp2(e_0) {
  e_0.currentTarget.style.transform = "scale(1)";
}
function _temp(e) {
  e.currentTarget.style.transform = "scale(1.08)";
}